import { useState } from "react";
import bankingService from "api/bankingService";
import {
  IFinancialOperation,
  ISettlementRequest,
  SettlementStatus,
  SettlementType,
} from "dto/Banking/IBanking";
import styles from "./SettlementForm.module.scss";

interface Props {
  operation: IFinancialOperation;
  onClose: () => void;
}

const isoDate = (iso?: string) => (iso ? iso.slice(0, 10) : "");

/**
 * Formatka odnotowania operacji finansowej na koncie rozrachunkowym.
 * Slide od lewej; pola input w stylu modali aplikacji; pola wstępnie wypełnione
 * danymi operacji bankowej.
 */
const SettlementForm = ({ operation, onClose }: Props) => {
  const save = bankingService.useSaveSettlement();

  const [settlementAccount, setSettlementAccount] = useState("");
  const [type, setType] = useState<SettlementType>(
    operation.Direction === "Credit" ? "Receivable" : "Payable",
  );
  const [amount, setAmount] = useState(String(operation.Amount ?? ""));
  const [operationDate, setOperationDate] = useState(isoDate(operation.BookingDate));
  const [dueDate, setDueDate] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [counterparty, setCounterparty] = useState(operation.CounterpartyName ?? "");
  const [description, setDescription] = useState(operation.Title ?? "");
  const [status, setStatus] = useState<SettlementStatus>("Open");
  const [error, setError] = useState(false);

  const handleSave = () => {
    if (!settlementAccount.trim()) {
      setError(true);
      return;
    }
    const request: ISettlementRequest = {
      SettlementAccount: settlementAccount.trim(),
      Type: type,
      Amount: Number(amount) || 0,
      OperationDate: operationDate || undefined,
      DueDate: dueDate || undefined,
      DocumentNumber: documentNumber.trim() || undefined,
      CounterpartyName: counterparty.trim() || undefined,
      Description: description.trim() || undefined,
      Status: status,
    };
    save.mutate(
      { operationId: operation.Id, request },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.panel}>
        <div className={styles.header}>
          <span className={styles.title}>Rozrachunek</span>
          <button className={styles.closeBtn} onClick={onClose} title="Zamknij">
            ✕
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.fieldLabel}>Konto rozrachunkowe *</div>
          <input
            className={`${styles.input} ${error && !settlementAccount.trim() ? styles.inputError : ""}`}
            value={settlementAccount}
            onChange={(e) => setSettlementAccount(e.target.value)}
            placeholder="np. 201 — Rozrachunki z odbiorcami"
          />
          {error && !settlementAccount.trim() && (
            <div className={styles.errorText}>Podaj konto rozrachunkowe.</div>
          )}

          <div className={styles.fieldLabel}>Typ rozrachunku</div>
          <select
            className={styles.input}
            value={type}
            onChange={(e) => setType(e.target.value as SettlementType)}
          >
            <option value="Receivable">Należność</option>
            <option value="Payable">Zobowiązanie</option>
          </select>

          <div className={styles.fieldLabel}>
            Kwota <span className={styles.currencyHint}>{operation.Currency}</span>
          </div>
          <input
            className={styles.input}
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />

          <div className={styles.row}>
            <div>
              <div className={styles.fieldLabel}>Data operacji</div>
              <input
                className={styles.input}
                type="date"
                value={operationDate}
                onChange={(e) => setOperationDate(e.target.value)}
              />
            </div>
            <div>
              <div className={styles.fieldLabel}>Termin płatności</div>
              <input
                className={styles.input}
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.fieldLabel}>Numer dokumentu</div>
          <input
            className={styles.input}
            value={documentNumber}
            onChange={(e) => setDocumentNumber(e.target.value)}
            placeholder="np. FV 12/2026"
          />

          <div className={styles.fieldLabel}>Kontrahent</div>
          <input
            className={styles.input}
            value={counterparty}
            onChange={(e) => setCounterparty(e.target.value)}
          />

          <div className={styles.fieldLabel}>Opis</div>
          <textarea
            className={styles.input}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />

          <div className={styles.fieldLabel}>Status</div>
          <select
            className={styles.input}
            value={status}
            onChange={(e) => setStatus(e.target.value as SettlementStatus)}
          >
            <option value="Open">Nierozliczony</option>
            <option value="PartiallySettled">Częściowo rozliczony</option>
            <option value="Settled">Rozliczony</option>
          </select>
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Anuluj
          </button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={save.isLoading}>
            Zapisz rozrachunek
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettlementForm;
